import cn from 'classnames';
import { Suspense, type ReactNode } from 'react';
import { useBlockHostMutation, useSuspenseHostQuery } from '#api/hosts.queries.ts';
import { Banner } from '#components/atoms/banner/Banner.tsx';
import { Code } from '#components/atoms/code/Code.tsx';
import { ConfirmDialog } from '#components/patterns/confirmations/confirm-dialog/ConfirmDialog.tsx';
import type { Host } from '#dn-types/host.ts';
import { useActiveCount } from '#hooks/useActiveCount.ts';
import { useIsManagedLighthouseCovered } from '#hooks/useIsManagedLighthouseCovered.ts';
import { hostKind, isInfraHost, type InfraHost } from '#util/host.ts';

type Props = {
  hostId: string;
  onClose: () => void;
};

export function BlockHostDialog({ hostId, onClose }: Props) {
  const host = useSuspenseHostQuery(hostId).data.data;

  // Only lighthouses and relays carry a last-one-active banner, which needs the per-network count and
  // managed-lighthouse coverage. Plain hosts skip those fetches entirely. The Suspense boundary keeps
  // the count fetch from bubbling to the page boundary, which would blank the page on dialog open.
  return isInfraHost(host) ?
      <Suspense fallback={null}>
        <BlockInfraDialog host={host} onClose={onClose} />
      </Suspense>
    : <BlockDialog host={host} onClose={onClose} banner={null} />;
}

/** Lighthouse/relay block dialog: adds the last-one-active banner (and its managed-covered variant). */
function BlockInfraDialog({ host, onClose }: { host: InfraHost; onClose: () => void }) {
  const kind = hostKind(host);
  const { isLastActive } = useActiveCount(host);
  const isMtlhCovered = useIsManagedLighthouseCovered(host.networkID);

  const banner =
    isLastActive ?
      isMtlhCovered ?
        <Banner variant="info" className="mb-24">
          {kind === 'lighthouse' ?
            'This is your only self-hosted lighthouse. Managed lighthouses will keep this network connected.'
          : 'This is your only dedicated relay. Managed lighthouses still relay traffic on this network.'}
        </Banner>
      : <Banner variant="alert" className="mb-24">
          {kind === 'lighthouse' ?
            'This is your only active lighthouse.'
          : 'This is your only active dedicated relay in this network.'}
        </Banner>
    : null;

  return <BlockDialog host={host} onClose={onClose} banner={banner} />;
}

function BlockDialog({ host, onClose, banner }: { host: Host; onClose: () => void; banner: ReactNode }) {
  const blockHost = useBlockHostMutation();
  const kind = hostKind(host);

  return (
    <ConfirmDialog
      title={<div className="truncate">Block {host.name}?</div>}
      disableAccept={blockHost.isPending || !host}
      acceptButtonText="Block"
      onCancel={onClose}
      onAccept={() => {
        blockHost.mutate(host.id, {
          onSuccess: onClose,
        });
      }}
    >
      {banner}
      <p className={cn('p', { 'm-0': kind === 'host' && !host.endpointOIDCUserID, 'mb-0': kind !== 'host' })}>
        Only block <Code className="wrap-anywhere">{host.name}</Code> if you believe it has been{' '}
        {host.endpointOIDCUserID ? 'lost or ' : ''}compromised. Once blocked, an update will be issued to all other
        hosts, lighthouses, and relays instructing them to reject any attempts to communicate with this {kind}.
      </p>
      {host.endpointOIDCUserID ?
        <p className="p m-0">
          To prevent the device owner from re-enrolling the device, revoke their credentials within the endpoint
          identity provider.
        </p>
      : null}
    </ConfirmDialog>
  );
}
